export const MAX_FILE_MB = 100
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024
const TOO_LITTLE_TEXT_CHARS = 40
const LINE_Y_TOLERANCE = 2

export interface ExtractProgress {
  current: number
  total: number
}

export interface ExtractedPdfText {
  text: string
  pageCount: number
  charCount: number
  wordCount: number
  warning: 'too_little_text' | null
}

export function validateFileSize(file: File): boolean {
  return file.size <= MAX_FILE_BYTES
}

export function txtFilename(originalName: string): string {
  const dot = originalName.lastIndexOf('.')
  const base = dot !== -1 ? originalName.slice(0, dot) : originalName
  return `${base || 'document'}.txt`
}

interface PdfJsModule {
  GlobalWorkerOptions: { workerSrc: string }
  getDocument: (src: { data: ArrayBuffer }) => { promise: Promise<PdfJsDocument> }
}

interface PdfJsDocument {
  numPages: number
  getPage: (n: number) => Promise<PdfJsPage>
  destroy: () => Promise<void>
}

interface PdfJsPage {
  getTextContent: () => Promise<PdfTextContent>
  cleanup: () => void
}

interface PdfTextContent {
  items: PdfTextItem[]
}

interface PdfTextItem {
  str: string
  transform: number[]
  hasEOL?: boolean
}

interface TextLine {
  y: number
  items: PdfTextItem[]
}

function itemX(item: PdfTextItem): number {
  return item.transform[4] ?? 0
}

function itemY(item: PdfTextItem): number {
  return item.transform[5] ?? 0
}

function textItemValue(item: PdfTextItem): string {
  return item.str.replace(/\s+/g, ' ').trim()
}

function pageText(items: PdfTextItem[]): string {
  const lines: TextLine[] = []
  for (const item of items) {
    const value = textItemValue(item)
    if (!value) continue

    const y = itemY(item)
    const line = lines.find((candidate) => Math.abs(candidate.y - y) <= LINE_Y_TOLERANCE)
    if (line) {
      line.items.push(item)
    } else {
      lines.push({ y, items: [item] })
    }
  }

  return lines
    .sort((a, b) => b.y - a.y)
    .map((line) =>
      line.items
        .sort((a, b) => itemX(a) - itemX(b))
        .map(textItemValue)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim(),
    )
    .filter(Boolean)
    .join('\n')
}

export interface ExtractOptions {
  file: File
  signal: AbortSignal
  onProgress: (progress: ExtractProgress) => void
}

export async function extractPdfText({
  file,
  signal,
  onProgress,
}: ExtractOptions): Promise<ExtractedPdfText> {
  if (signal.aborted) throw new DOMException('Aborted', 'AbortError')

  const pdfjs = (await import('pdfjs-dist')) as unknown as PdfJsModule
  const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl

  const bytes = await file.arrayBuffer()
  const doc = await pdfjs.getDocument({ data: bytes }).promise

  try {
    const pages: string[] = []
    onProgress({ current: 0, total: doc.numPages })

    for (let pageNumber = 1; pageNumber <= doc.numPages; pageNumber++) {
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError')

      const page = await doc.getPage(pageNumber)
      const content = await page.getTextContent()
      const text = pageText(content.items)
      if (text) pages.push(text)
      page.cleanup()

      onProgress({ current: pageNumber, total: doc.numPages })
    }

    const text = pages.join('\n\n').trim()
    const charCount = text.replace(/\s/g, '').length
    const wordCount = text ? text.split(/\s+/).filter(Boolean).length : 0
    const warning = charCount > 0 && charCount < TOO_LITTLE_TEXT_CHARS ? 'too_little_text' : null

    if (charCount === 0) {
      throw new Error('no_text_layer')
    }

    return {
      text,
      pageCount: doc.numPages,
      charCount,
      wordCount,
      warning,
    }
  } finally {
    await doc.destroy().catch(() => {})
  }
}

